import { useState, type CSSProperties, type JSX, type ReactNode } from 'react';

/**
 * A lightweight disclosure — a titled row that expands to reveal its children.
 * Collapsed by default (so heavy content like the login audit or the passkey
 * list doesn't dominate the drawer). Accessible: the trigger is a real button
 * with aria-expanded + aria-controls. Self-styled with tokens + fallbacks.
 */
export interface CollapsibleProps {
  title: ReactNode;
  children: ReactNode;
  /** Start open (default false — collapsed). */
  defaultOpen?: boolean;
  /** Optional right-aligned hint (e.g. a count) shown on the trigger row. */
  hint?: ReactNode;
}

let uid = 0;

export function Collapsible({
  title,
  children,
  defaultOpen = false,
  hint,
}: CollapsibleProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const [id] = useState(() => `cca-collapsible-${(uid += 1)}`);

  const trigger: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    background: 'transparent',
    border: 0,
    padding: '8px 0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-ink, #111827)',
    textAlign: 'left',
  };
  const chevron: CSSProperties = {
    display: 'inline-block',
    transition: 'transform var(--duration-fast, 120ms) var(--ease-standard, ease)',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
    fontSize: 10,
    color: 'var(--color-muted, #6b7280)',
    width: 12,
    flexShrink: 0,
  };

  return (
    <div>
      <button
        type="button"
        style={trigger}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true" style={chevron}>
          ▶
        </span>
        <span style={{ flex: 1 }}>{title}</span>
        {hint != null && (
          <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-muted, #6b7280)' }}>
            {hint}
          </span>
        )}
      </button>
      {open && (
        <div id={id} style={{ padding: '4px 0 8px' }}>
          {children}
        </div>
      )}
    </div>
  );
}
